/** An event's integrations, with their health, recent runs, and a way to connect more Scout data. */

import { Plug } from "lucide-react";
import { Link } from "react-router-dom";

import { listWorkflows } from "@/lib/api/workflows";
import { listIntegrations } from "@/lib/integrations/api";
import { healthOf, sendsTo, troubleFirst } from "@/lib/integrations/words";
import {
  Banner,
  Button,
  Card,
  Column,
  Empty,
  HealthPill,
  LinkButton,
  Section,
  SkeletonRows,
  Table,
  Td,
  Th,
} from "@/components/kit";

import { LastRun } from "./LastRun";
import { useCanWrite, useKeyed, useNow } from "./hooks";

export function EventIntegrations({ code }: { code: string }) {
  const now = useNow();
  const canWrite = useCanWrite();
  const list = useKeyed(`event|${code}`, () => listIntegrations(code));
  const workflows = useKeyed("workflows", () => listWorkflows());
  const names = new Map((workflows.data ?? []).map((row) => [row.code, row.name]));
  const connect = `/integrations/new?event=${encodeURIComponent(code)}`;
  const rows = troubleFirst(list.data ?? [], (item) => healthOf(item, { now }));

  return (
    <Section
      title="Integrations"
      note="Scout data coming into this event. Each one reads a Scout job and sends it to a workflow’s import."
      actions={
        canWrite ? (
          <LinkButton to={connect} tone="primary">
            <Plug size={15} aria-hidden />
            Connect Scout data
          </LinkButton>
        ) : undefined
      }
    >
      <Column gap="sm">
        {list.error && !list.data ? (
          <Banner tone="danger" title="This event’s integrations did not load." actions={<Button onClick={list.reload}>Try again</Button>}>
            {list.error}
          </Banner>
        ) : !list.data ? (
          <Card>
            <SkeletonRows rows={3} columns={4} label="Loading integrations" />
          </Card>
        ) : rows.length === 0 ? (
          <Card>
            <Empty
              title="No Scout data connected yet"
              note="Connect a Scout job to one of this event’s workflows. No code is needed."
              action={canWrite ? <LinkButton to={connect}>Connect Scout data</LinkButton> : undefined}
            />
          </Card>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Integration</Th>
                <Th>Health</Th>
                <Th>Recent runs</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((item) => (
                <tr key={item.code}>
                  <Td>
                    <Link to={`/integrations/${encodeURIComponent(item.code)}`} style={{ fontWeight: 600 }}>
                      {item.name}
                    </Link>
                    <div style={{ color: "var(--ink-2)", fontSize: "var(--t-sm)", marginTop: 2 }}>
                      {sendsTo(names.get(item.workflow) ?? item.workflow, item.importCommand)}
                    </div>
                  </Td>
                  <Td>
                    <HealthPill health={healthOf(item, { now })} count={item.openAnswers || undefined} />
                  </Td>
                  <Td>
                    <LastRun code={item.code} lastRunAt={item.lastRunAt} now={now} />
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {list.data && rows.length > 0 && (
          <p style={{ margin: 0, fontSize: "var(--t-sm)" }}>
            <Link to="/integrations">See every integration</Link>
          </p>
        )}
      </Column>
    </Section>
  );
}
